/**
 * src/hooks/useEntityMic.js — Inline entity mic recording hook
 *
 * Provides MediaRecorder-based recording that feeds audio into
 * captureVoice() with pre-filled context (contextType + contextId).
 *
 * States: idle | recording | processing | done | error
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { emitVoiceEvent } from '../lib/voiceEvents';
import { registerMic, releaseMic } from '../lib/voice/micRegistry';
// TICKET-VOICE-QUEUE-PHANTOM-RESIDUAL (2026-05-06): use the atomic
// helper that emits voice:record_stop AND enqueues with one shared
// recording_id. Calling enqueueVoiceSession directly here without
// passing recording_id is the bug that left a white "Processing..."
// phantom card on every mic-recorded voice note.
import { enqueueWithBreadcrumb } from '../lib/voice/enqueueWithBreadcrumb';
// TICKET-CODEX-3391901395: used to gate the eager voice:record_stop emit in
// stopRecording — project-upload scopes must not emit an optimistic breadcrumb
// here because enqueueWithBreadcrumb already skips it for them (no idempotency_key
// threading → phantom card that can never reconcile). For in-app scopes the emit
// is kept so the iOS 3-4s gap (CEO 2026-06-11) still gets immediate UI feedback.
import { PROJECT_UPLOAD_SCOPES } from '../lib/voice/voiceScopes';
// TICKET-1079 (CEO 2026-10-02): a 110-byte header-only recording reached Whisper
// and was retried 10x. Shared guards decide what is audio; telemetry records why.
import { pickRecorderMime, isTooSmallAudio, extensionForMime, deviceInfo } from '../lib/voice/audioGuards';
import { logVoiceEvent } from '../lib/voice/voiceLogger';

function newRecordingId() {
  return `entity-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function useEntityMic({ contextType, contextId, contextHint }) {
  const [micState, setMicState] = useState('idle'); // idle | recording | processing | done | error
  const [errorMsg, setErrorMsg] = useState(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  // TICKET-064: capture MediaRecorder elapsed time so the History card's
  // compact row renders a real duration. Set on .start(), read on .onstop().
  const recordingStartedAtRef = useRef(null);
  // CEO 2026-06-11: recording_id generated at TAP-STOP (stopRecording) so the
  // optimistic placeholder fires the instant the user stops — not after the
  // 3-4s MediaRecorder blob-assembly on iOS. onstop reuses this same id so the
  // real voice_records row reconciles by idempotency_key.
  const pendingRecordingIdRef = useRef(null);

  // Release microphone on unmount — prevents Chrome red-dot persisting after nav.
  useEffect(() => {
    return () => {
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state === 'recording') rec.stop();
      } catch { /* noop */ }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch { /* noop */ } });
        streamRef.current = null;
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setErrorMsg(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // TICKET-1079: the first container this browser can record (Safari = mp4),
      // not a hard-coded webm/opus.
      const recorder = new MediaRecorder(stream, {
        mimeType: pickRecorderMime(),
        audioBitsPerSecond: 48000,
      });
      mediaRecorderRef.current = recorder;
      // Global mic registry — auto-released on tab hide / unload so the
      // red-dot bug + silent mobile chunk leak can't recur. See
      // src/lib/voice/micRegistry.js.
      registerMic(stream, recorder, `useEntityMic:${contextType}:${contextId}`);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Stop all tracks + drop registry entry so the red-dot clears.
        releaseMic(stream);
        streamRef.current = null;
        mediaRecorderRef.current = null;

        // TICKET-064: compute MediaRecorder elapsed seconds BEFORE clearing
        // chunks/refs. Null when start time wasn't recorded (defensive — the
        // recorder.start() handler sets it, so this is belt + suspenders).
        const startedAt = recordingStartedAtRef.current;
        recordingStartedAtRef.current = null;
        const durationSec = typeof startedAt === 'number'
          ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
          : null;

        // Reuse the id emitted at tap-stop (stopRecording) so the optimistic
        // row already on screen reconciles when the real voice_records row
        // lands (idempotency_key match). Defensive fallback if somehow unset.
        const recordingId = pendingRecordingIdRef.current || newRecordingId();
        pendingRecordingIdRef.current = null;

        // TICKET-1079: build the blob first and judge it by SIZE, not by chunk
        // count — a header-only file is one non-empty chunk with no audio in it.
        const mime = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] });
        const empty = chunksRef.current.length === 0 || isTooSmallAudio(blob);
        logVoiceEvent('voice.record.stop', empty ? 'error' : 'ok',
          empty ? `empty recording (${blob.size} bytes)` : `recorded ${blob.size} bytes`, {
            trace_id: recordingId,
            scope_type: contextType,
            scope_id: contextId || null,
            bytes: blob.size,
            chunks: chunksRef.current.length,
            duration_sec: durationSec,
            mime,
            empty,
            ...deviceInfo(),
          });

        if (empty) {
          // Nothing usable captured — clear the optimistic placeholder we already
          // showed at tap-stop so it can't linger as a phantom card, and tell the
          // user instead of handing an empty file to transcription.
          emitVoiceEvent('voice:finalize_error', {
            contextType, contextId, recordingId, error: 'empty recording',
          });
          setErrorMsg('Nothing was recorded — check the microphone and try again');
          setMicState('error');
          setTimeout(() => setMicState('idle'), 4000);
          return;
        }

        // V-5: hand the blob to the queue and return to idle immediately.
        // The worker (queueWorker singleton) drains and calls captureVoice
        // server-side; the VoiceQueueTray shows progress; useVoiceBreadcrumbs
        // confirms the optimistic row via realtime once voice_records lands.
        // Mic is free for the next recording within milliseconds — fixing
        // Chris's on-site "channel busy" report (2026-04-30).
        try {
          // TICKET-VOICE-QUEUE-PHANTOM-RESIDUAL: helper emits
          // voice:record_stop AND enqueues with one shared recording_id,
          // so the optimistic row in useVoiceBreadcrumbs gets reconciled
          // when the real voice_records row lands (idempotency_key match).
          await enqueueWithBreadcrumb({
            blob,
            contextType,
            contextId,
            contextHint: contextHint || undefined,
            fileName: `${contextType}-${recordingId}.${extensionForMime(mime)}`,
            recording_id: recordingId,
            duration_sec: durationSec ?? undefined,
          });
          // Brief 'done' flash for confirmation, then idle so the user can
          // tap again immediately. The tray takes over visual progress.
          setMicState('done');
          setTimeout(() => setMicState('idle'), 600);
        } catch (err) {
          console.error('[useEntityMic] enqueue error:', err);
          setErrorMsg(err.message || 'Failed to queue recording');
          setMicState('error');
          emitVoiceEvent('voice:finalize_error', {
            contextType, contextId, recordingId, error: err.message || 'Failed to queue recording',
          });
          setTimeout(() => setMicState('idle'), 4000);
        }
      };

      recorder.start();
      // TICKET-064: stamp recording start so onstop can compute elapsed
      // seconds and thread duration_sec through to voice_uploads.
      recordingStartedAtRef.current = Date.now();
      setMicState('recording');
      emitVoiceEvent('voice:record_start', { contextType, contextId });
    } catch (err) {
      console.error('[useEntityMic] getUserMedia error:', err);
      setErrorMsg('Microphone access denied');
      setMicState('error');
      setTimeout(() => setMicState('idle'), 3000);
    }
  }, [contextType, contextId, contextHint]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'recording') {
      // CEO 2026-06-11: fire the optimistic placeholder the INSTANT stop is
      // tapped — emit voice:record_stop BEFORE MediaRecorder.stop() finalizes
      // the blob (the 3-4s iOS gap reported). useVoiceBreadcrumbs inserts the
      // placeholder synchronously; onstop reuses this id so the real row
      // reconciles. The mic path threads recording_id -> voice_records
      // .idempotency_key end-to-end (server requires it), so this is
      // phantom-safe for in-app scopes (task / deal / bundle / general).
      //
      // TICKET-CODEX-3391901395: project-upload scopes are excluded here because
      // the project server pipeline does NOT thread the recording_id through to
      // voice_records.idempotency_key, so the optimistic row can never reconcile
      // and would linger as a phantom card. enqueueWithBreadcrumb already handles
      // the skip for those scopes — this guard makes stopRecording consistent with it.
      const recordingId = newRecordingId();
      pendingRecordingIdRef.current = recordingId;
      if (!PROJECT_UPLOAD_SCOPES.has(contextType)) {
        emitVoiceEvent('voice:record_stop', { contextType, contextId, recordingId });
      }
      rec.stop();
    }
  }, [contextType, contextId]);

  const toggle = useCallback(() => {
    if (micState === 'recording') {
      stopRecording();
    } else if (micState === 'idle') {
      startRecording();
    }
  }, [micState, startRecording, stopRecording]);

  return { micState, errorMsg, toggle };
}
